<template>
  <div 
    v-if="!getCurrentDiscussion.audio_discussion_is_enabled || (getCurrentDiscussion.audio_discussion_is_enabled && joinedAudioDiscussion)" 
    class="flex bg-white flex-col space-y-2 lg:space-y-0 lg:flex-row justify-between items-center border border-solid border-gray-200 rounded-lg px-1.5 py-1.5"
  >
    <!-- ESQUERDA -->
    <div class="flex items-center space-x-1.5 flex-wrap md:flex-nowrap overflow-x-auto md:overflow-visible">
      <button
        v-if="discussion_is_live"
        @click="$emit('toggleFullscreen')"
        class="border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        aria-label="Fullscreen"
        title="Fullscreen"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i class="fa-solid fa-expand" />
        </div>
      </button>

      <button
        @click="handleOpenPolls()"
        class="relative border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3 py-1"
        aria-label="Polls"
        title="Polls"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i class="fa-solid fa-chart-pie mt-0.5"></i>
          <span class="hidden md:inline">Polls</span>
          <div v-if="!hasTeacherPermissionsInCourse && polls_unawswered" class="absolute -top-1 -right-1 bg-pinky w-2 h-2 rounded-full"></div>
        </div>
      </button>

      <button
        @click="handleOpenCriteria()"
        class="relative border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3 py-1"
        aria-label="View Criteria"
        title="View Criteria"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i class="fa-solid fa-list-check"></i>
        </div>
      </button>

      <button
        v-if="discussion_is_live && getCurrentDiscussion.audio_discussion_is_enabled" 
        @click="handleChangeVideoStatus()"
        :disabled="isTappingOut" 
        class="border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1 disabled:opacity-50"
        :aria-label="statusCamera ? 'Turn camera off' : 'Turn camera on'"
        :title="statusCamera ? 'Turn camera off' : 'Turn camera on'"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i :class="statusCamera === true ? 'fa-video' : 'fa-video-slash text-red-500'" class="fa-solid"></i>
        </div>
      </button>

      <button 
        v-if="present_in_circle && getCurrentDiscussion.audio_discussion_is_enabled" 
        @click="handleChangeMicroStatus()" 
        :disabled="isTappingOut" 
        class="text-center w-8 md:w-12 h-8 rounded-md px-0 md:px-3.5 py-1 hover:bg-gray-100 text-gray-700 border border-solid border-gray-300 disabled:opacity-50"
        :aria-label="statusMicro ? 'Unmute' : 'Mute'"
        :title="statusMicro ? 'Unmute' : 'Mute'"
      >
        <div class="flex items-center justify-center md:justify-start space-x-0 md:space-x-2">
          <i :class="statusMicro === true ? 'fa-microphone-slash text-red-500' : 'fa-microphone ml-1'" class="fa-solid" />
        </div>
      </button>

      <div v-if="isCapturingTranscription === true" class="flex items-center px-2 text-gray-600">
        <div class="flex items-center space-x-0.5 h-6 rounded-full w-5">
          <div v-for="n in 3" :key="n" class="w-1 bg-green-700 rounded-full transition-all duration-100" :style="{ height: getBarHeight(n) }"></div>
        </div>
      </div>
    </div>

    <!-- DIREITA -->
    <div v-if="discussion_is_live" class="flex space-x-2 items-center flex-wrap md:flex-nowrap">
      <!-- TAP IN (quando não está no círculo) -->
      <div v-if="!present_in_circle" class="flex items-center justify-center space-x-0.5 md:space-x-1.5">
        <button :disabled="disabledTapIn || teacher_tap_in" @click="tapIn('new_idea')" class="hover:bg-primary disabled:opacity-50 text-primary px-2 md:px-3.5 py-1 h-8 rounded-md hover:text-white" aria-label="New Idea" title="New Idea">
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-play"></i>
            <span class="font-poppins text-xs hidden md:inline">New Idea</span>
          </div>
        </button>
        <button :disabled="disabledTapIn || teacher_tap_in" @click="tapIn('challenge')" class="hover:bg-pinky disabled:opacity-50 hover:text-white px-2 md:px-3.5 py-1 h-8 rounded-md text-pinky" aria-label="Challenge" title="Challenge">
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-circle"></i>
            <span class="font-poppins text-xs hidden md:inline">Challenge</span>
          </div>
        </button>
        <button :disabled="disabledTapIn || teacher_tap_in" @click="tapIn('build_on')" class="text-emerald-600 disabled:opacity-50 hover:bg-emerald-600 hover:text-white px-2 md:px-3.5 py-1 h-8 rounded-md" aria-label="Build On" title="Build On">
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-square"></i>
            <span class="font-poppins text-xs hidden md:inline">Build On</span>
          </div>
        </button>
        <button :disabled="disabledTapIn || teacher_tap_in" @click="tapIn('question')" class="hover:bg-darkBlue-dark disabled:opacity-50 text-darkBlue-dark px-2 md:px-3.5 h-8 py-1 rounded-md hover:text-white" aria-label="Question" title="Question">
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-circle-question"></i>
            <span class="font-poppins text-xs hidden md:inline">Question</span>
          </div>
        </button>
      </div>

      <!-- PRESENTE NO CÍRCULO -->
      <div v-else class="flex items-center justify-center space-x-1 md:space-x-1.5">
        <!-- Tap Out -->
        <button :disabled="isTappingOut === true" @click="tapOutOfCircle()" class="bg-red-500 disabled:opacity-50 hover:bg-red-600 px-2 md:px-3.5 py-1 h-8 rounded-md text-white" aria-label="Tap Out" title="Tap Out">
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-arrow-up-from-bracket rotate-270" />
            <span class="font-poppins text-xs hidden md:inline">Tap Out</span>
          </div>
        </button>

        <!-- Turn Description (esconde em ecrãs muito pequenos) -->
        <div class="hidden sm:flex h-8 w-40 md:w-64">
          <button
            v-if="!edit_description_mode"
            @click="editDescription()"
            class="text-xs text-gray-500 text-left hover:bg-gray-100 border border-solid border-gray-300 cursor-text rounded px-2 md:px-3.5 w-full h-full truncate"
            :title="my_current_turn.description || my_turn_description_label"
          > 
            <i class="fa-solid fa-quote-left mr-1"></i>
            <span class="truncate">{{ my_current_turn.description || my_turn_description_label }}</span>
          </button>
          <input
            v-else
            v-model="turn_description"
            type="text"
            ref="descriptionInput"
            maxlength="70"
            @keyup.enter="updateTurnDescription"
            @blur="updateTurnDescription"
            class="text-xs bg-input text-gray-500 outline-none px-2 w-full h-full rounded"
          />
        </div>

        <!-- Current Turn (labels escondidas < md) -->
        <div v-if="current_speaker" class="flex items-center space-x-1">
          <button :disabled="disabledTapIn || teacher_tap_in" v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'New Idea'" class="bg-primary disabled:opacity-50 px-2 md:px-3.5 py-1 h-8 rounded-md text-white" aria-label="New Idea" title="New Idea">
            <div class="flex items-center space-x-0 md:space-x-2">
              <i class="fa-solid fa-play"></i>
              <span class="font-poppins text-xs hidden md:inline">New Idea</span>
            </div>
          </button>
          <button :disabled="disabledTapIn || teacher_tap_in" v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Challenge'" class="bg-pinky disabled:opacity-50 text-white px-2 md:px-3.5 py-1 h-8 rounded-md" aria-label="Challenge" title="Challenge">
            <div class="flex items-center space-x-0 md:space-x-2">
              <i class="fa-solid fa-circle"></i>
              <span class="font-poppins text-xs hidden md:inline">Challenge</span>
            </div>
          </button>
          <button :disabled="disabledTapIn || teacher_tap_in" v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Build On'" class="bg-emerald-600 disabled:opacity-50 text-white px-2 md:px-3.5 py-1 h-8 rounded-md" aria-label="Build On" title="Build On">
            <div class="flex items-center space-x-0 md:space-x-2">
              <i class="fa-solid fa-square"></i>
              <span class="font-poppins text-xs hidden md:inline">Build On</span>
            </div>
          </button>
          <button :disabled="disabledTapIn || teacher_tap_in" v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Question'" class="bg-darkBlue-dark disabled:opacity-50 px-2 md:px-3.5 h-8 py-1 rounded-md text-white" aria-label="Question" title="Question">
            <div class="flex items-center space-x-0 md:space-x-2">
              <i class="fa-solid fa-circle-question"></i>
              <span class="font-poppins text-xs hidden md:inline">Question</span>
            </div>
          </button>
          <button :disabled="disabledTapIn || teacher_tap_in" v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Add by teacher'" class="bg-orange disabled:opacity-50 px-2 md:px-3.5 h-8 py-1 rounded-md text-white" aria-label="Add by teacher" title="Add by teacher">
            <div class="flex items-center space-x-0 md:space-x-2">
              <i class="fa-solid fa-chalkboard-user"></i>
              <span class="font-poppins text-xs hidden md:inline">Add By Teacher</span>
            </div>
          </button>
        </div>

        <!-- pl-menu quando NÃO és o current speaker -->
        <div v-if="!current_speaker">
          <pl-menu direction="right" :menuSize="3">
            <!-- New Idea trigger -->
            <button
              v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'New Idea'"  
              @mouseenter="isCurrentTurnHover = true" 
              @mouseleave="isCurrentTurnHover = false" 
              slot="menu_trigger" 
              class="bg-primary px-2 md:px-3.5 py-1 h-8 rounded-md text-white"
            >
              <div class="flex justify-center items-center space-x-0 md:space-x-2">
                <i v-if="isCurrentTurnHover === false" class="fa-solid fa-play"></i>
                <span class="font-poppins text-xs"><span class="hidden md:inline">{{isCurrentTurnHover ? 'Change' : 'New Idea'}}</span></span>
              </div>
            </button>

            <!-- Challenge trigger -->
            <button 
              @mouseenter="isCurrentTurnHover = true" 
              @mouseleave="isCurrentTurnHover = false" 
              slot="menu_trigger" 
              v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Challenge'" 
              class="bg-pinky text-white px-2 md:px-3.5 text-center py-1 h-8 rounded-md"
            >
              <div class="flex text-center justify-center items-center space-x-0 md:space-x-2">
                <i v-if="isCurrentTurnHover === false" class="fa-solid fa-circle"></i>
                <span class="font-poppins text-xs"><span class="hidden md:inline">{{isCurrentTurnHover ? 'Change' : 'Challenge'}}</span></span>
              </div>
            </button>

            <!-- Build On trigger -->
            <button 
              @mouseenter="isCurrentTurnHover = true" 
              @mouseleave="isCurrentTurnHover = false" 
              slot="menu_trigger" 
              v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Build On'" 
              class="bg-emerald-600 text-white px-2 md:px-3.5 py-1 h-8 rounded-md"
            >
              <div class="flex justify-center items-center space-x-0 md:space-x-2">
                <i v-if="isCurrentTurnHover === false" class="fa-solid fa-square"></i>
                <span class="font-poppins text-xs"><span class="hidden md:inline">{{isCurrentTurnHover ? 'Change' : 'Build On'}}</span></span>
              </div>
            </button>

            <!-- Question trigger -->
            <button 
              @mouseenter="isCurrentTurnHover = true" 
              @mouseleave="isCurrentTurnHover = false" 
              slot="menu_trigger" 
              v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Question'" 
              class="bg-darkBlue-dark px-2 md:px-3.5 h-8 py-1 rounded-md text-white"
            >
              <div class="flex items-center justify-center space-x-0 md:space-x-2">
                <i v-if="isCurrentTurnHover === false" class="fa-solid fa-circle-question"></i>
                <span class="font-poppins text-xs"><span class="hidden md:inline">{{isCurrentTurnHover ? 'Change' : 'Question'}}</span></span>
              </div>
            </button>

            <!-- Add by teacher trigger -->
            <button
              @mouseenter="isCurrentTurnHover = true" 
              @mouseleave="isCurrentTurnHover = false" 
              slot="menu_trigger" 
              v-if="generateTurnPhrases(my_current_turn.turn_type).title === 'Add by teacher'" 
              class="bg-orange px-2 md:px-3.5 h-8 py-1 rounded-md text-white"
            >
              <div class="flex items-center justify-center space-x-0 md:space-x-2">
                <i v-if="isCurrentTurnHover === false" class="fa-solid fa-chalkboard-user"></i>
                <span class="font-poppins text-xs"><span class="hidden md:inline">{{isCurrentTurnHover ? 'Change' : 'Add by teacher'}}</span></span>
              </div>
            </button>

            <!-- menu content -->
            <ul menu-content class="text-xs overflow-x-hidden">
              <li v-for="(type) in turn_types" :key="type" @click="updateTurnType(type)" class="menu-option font-poppins">
                <button :disabled="disabledTapIn || teacher_tap_in || my_current_turn.turn_type === type" class="flex items-center w-full h-full disabled:opacity-50">
                  <div v-html="determineTurnTypeIconHTML(type, true)" class="mr-0.5 w-4" />
                  <p>{{ generateTurnPhrases(type).title }}</p>
                </button>
              </li>
            </ul>
          </pl-menu>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import {
    tapOutOfCircle,
    updateTurn,
  } from 'services/liveDiscussion/circle/circleServices.js';
  import toggleOwnAudio from 'services/liveDiscussion/toggleOwnAudio.js';
  import toggleOwnVideo from 'services/liveDiscussion/toggleOwnVideo.js';
  import { EventBus } from 'utils/event-bus';

  export default {
    data() {
      return {
        turn_types: ['new_idea', 'challenge', 'build_on', 'question'],
        disabledTapIn: false,
        isCapturingTranscription: false,
        default_turn: {
          id: null,
          participation: {
            id: null,
            user: {
              id: null,
              avatar_url: null,
              name: null,
            },
          },
          participation_id: null,
          turn_type: 'hand_raise',
          encouraged_by: [],
        },
        edit_description_mode: false,
        turn_description: '',
        isCurrentTurnHover: false,
        isTappingOut: false,
        micLevel: 0,
      };
    },
    props: {
      joinedAudioDiscussion: {
        type: Boolean,
        default: false,
      },
      statusMicro: {
        type: Boolean,
      },
      statusCamera: {
        type: Boolean,
      }
    },
    computed: {
      ...mapGetters([
        'hasTeacherPermissionsInCourse',
        'getCurrentDiscussion',
        'getProfile',
        'getCourseDetail',
      ]),
      discussion_is_live() {
        return this.getCurrentDiscussion.is_live;
      },
      audio_video_is_enabled() {
        return this.getCurrentDiscussion.audio_discussion_is_enabled;
      },
      my_participation() {
        return this.getCurrentDiscussion.participations.find(
          (p) => p.user_id === this.getProfile.id
        );
      },
      present_in_circle() {
        const circle = this.getCurrentDiscussion.turns_in_circle;
        const presence = circle.find(
          (turn) =>
            turn.participation.user.id === this.getProfile.id &&
            turn.turn_type !== 'video_audio_on'
        );
        return Boolean(presence);
      },
      my_current_turn() {
        return (
          this.getCurrentDiscussion.turns_in_circle.find(
            (t) => t.participation.user.id === this.getProfile.id
          ) || this.default_turn
        );
      },
      my_turn_description_label() {
        return this.generateTurnPhrases(this.my_current_turn.turn_type)
          .askToInputDescription;
      },
      polls_unawswered() {
        const polls = this.getCurrentDiscussion.polls;
        let count = 0;
        polls.forEach((poll) => {
          if (
            poll.status === 'live' &&
            !this.getPollVoters(poll).includes(this.getProfile.id)
          ) {
            count += 1;
          }
        });
        return count > 0 ? true : false;
      },
      students_with_video_on() {
        return this.getCurrentDiscussion.participations.filter(
          (p) => p.video_is_enabled
        );
      },
      teacher_tap_in() {
        return this.getCurrentDiscussion.teacher_tapped_in_id;
      },
      current_speaker() {
        return (
          this.my_current_turn.participation.user.id ===
          this.getCurrentDiscussion.currently_active
        );
      },
    },
    mounted: async function () {
      EventBus.$on('capturing-transcription', (value) => {
        this.isCapturingTranscription = value;
      });
    },
    methods: {
      getBarHeight(n) {
        const activeBars = Math.round((this.micLevel / 100) * 5);
        return n <= activeBars ? `${Math.max(30, (this.micLevel / 100) * 100)}%` : '20%';
      },
      handleOpenCriteria: async function() {
        EventBus.$emit('handleOpenCriteria');
      },
      handleOpenPolls: async function() {
        EventBus.$emit('handleOpenPolls');
      },
      handleChangeVideoStatus: async function() {
        this.$emit('handleChangeVideoStatus');
      },
      handleChangeMicroStatus: async function() {
        this.$emit('handleChangeMicroStatus');
      },
      editDescription: function () {
        this.edit_description_mode = true;
        /* Set Input With Focus */
        window.requestAnimationFrame(() => this.$refs.descriptionInput.focus());
      },
      determineTurnTypeIconHTML(type, update_turn) {
        let icon = `<i class="fa-solid `;
        let img = `<img class="h-8 w-8" src="/static/live_discussion/ld_question_icon.svg" />`;
        let size = 'text-xs';
        if (update_turn) {
          img = `<img class="h-4 w-4" src="/static/live_discussion/ld_question_icon.svg" />`;
          size = 'text-sm';
        }
        switch (type) {
          case 'new_idea':
            icon += `fa-play ${size} mr-2.5 text-primary"`;
            break;
          case 'build_on':
            icon += `fa-square ${size} mr-2.5 text-green-700"`;
            break;
          case 'challenge':
            icon += `fa-circle ${size} mr-2.5 text-pinky"`;
            break;
          case 'question':
            icon += `fa-circle-question mr-2.5 ${size} text-blueGray-700"`;
            break;
          default:
            icon = '';
        }
        if (icon) {
          icon += '></i>';
          return icon;
        } else if (img && type === 'question') {
          return img;
        } else {
          return '';
        }
      },
      generateTurnPhrases(turnType) {
        let title = '';
        let askToInputDescription = '';
        switch (turnType) {
          case 'new_idea':
            title = this.$t('new_idea'); //'New Idea';
            askToInputDescription = 'What is your new idea?';
            break;
          case 'build_on':
            title = this.$t('build_on'); //'Build On';
            askToInputDescription = 'Who/ What are you building on?';
            break;
          case 'challenge':
            title = this.$t('challenge'); //'Challenge';
            askToInputDescription = 'Who/ What are you challenging?';
            break;
          case 'question':
            title = this.$t('question'); //'Question';
            askToInputDescription = 'Who/ What are you questioning?';
            break;
          case 'hand_raise':
            title = this.$t('added_by_teacher'); //'Added by teacher';
            askToInputDescription = 'What would you like to share?';
            break;
        }
        return {
          title,
          askToInputDescription,
        };
      },
      autoTurnSpeaker: async function () {
        EventBus.$emit('queueLoading', true);
        await this.$store
          .dispatch('autoTurnSpeaker', {
            discussion_id: this.getCurrentDiscussion.id,
            hand_raise_type: 'hand_raise_type',
          })
          .then((res) => {
            if (res.failedReason || res.error)
              EventBus.$emit('queueLoading', false);
          })
          .catch(() => EventBus.$emit('queueLoading', false));
      },
      async tapIn(type) {
        if (this.isTappingOut === true) return;
        this.edit_description_mode = false;
        this.turn_description = '';
        this.disabledTapIn = true;
        setTimeout(() => {
          this.disabledTapIn = false;
        }, 1000 * 10);
        return this.$store.dispatch('tapIn', { discussion_id: this.getCurrentDiscussion.id, turn_type: type }).then(({ data }) => {
          if (data) {
            if (this.getCurrentDiscussion.auto_turn && !this.getCurrentDiscussion.currently_active && !this.getCurrentDiscussion.queue.length) {
              this.autoTurnSpeaker();
            }
          }
        });
      },
      async tapOutOfCircle() {
        if (this.isTappingOut === false) {
          this.isTappingOut = true;
          const participation = this.my_participation;
          if (this.audio_video_is_enabled) {
            if (participation.audio_is_enabled) await this.toggleOwnAudio();
            if (participation.video_is_enabled) await this.toggleOwnVideo();
          }
          if (this.getCurrentDiscussion.currently_active === this.getProfile.id) {
            await this.$store.dispatch('tapOut', { discussion_id: this.getCurrentDiscussion.id, user_id: this.getProfile.id });
            setTimeout(() => {
              this.isTappingOut = false;
            }, 1500);
          } else {
            await tapOutOfCircle(this.getCurrentDiscussion.id, this.getProfile.id);
            setTimeout(() => {
              this.isTappingOut = false;
            }, 1500);
          }
          
        }
      },
      updateTurnDescription() {
        updateTurn(this.my_current_turn.id, {
          description: this.turn_description,
        }).then(() => {
          this.edit_description_mode = false;
        });
      },
      getPollVoters(poll) {
        return poll.votes.map((v) => v.user_id);
      },
      toggleOwnAudio() {
        return toggleOwnAudio(this.$route.params.liveDiscussionID);
      },
      toggleOwnVideo() {
        return toggleOwnVideo(this.$route.params.liveDiscussionID);
      },
      updateTurnType(type) {
        if (this.current_speaker) {
          return;
        }
        this.disabledTapIn = true;
        setTimeout(() => {
          this.disabledTapIn = false;
        }, 1000 * 10);
        updateTurn(this.my_current_turn.id, {
          turn_type: type,
        });
      },
    },
  };
</script>



// WEBPACK FOOTER //
// LDDiscussionStudentBar.vue?e8801706